import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Filter, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { formatPrice } from '@/lib/api';
import { api } from '@/lib/api';

// Extracted outside to avoid re-creation on every render
const FilterSection = ({ id, title, isOpen, onToggle, children }) => (
  <Collapsible 
    open={isOpen} 
    onOpenChange={(open) => onToggle(id, open)}
    className="border-b border-border pb-4"
  >
    <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:text-primary transition-colors">
      <span className="text-sm font-semibold uppercase tracking-wider">{title}</span>
      <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </CollapsibleTrigger>
    <CollapsibleContent className="pt-3 space-y-3">
      {children}
    </CollapsibleContent>
  </Collapsible>
);

export const FilterSidebar = ({ 
  filters, 
  filterOptions, 
  onFilterChange, 
  onClearFilters,
  vehicleCount = 0 
}) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [searchPreviewCount, setSearchPreviewCount] = useState(null);
  const [searchPreviewLoading, setSearchPreviewLoading] = useState(false);
  const debounceRef = useRef(null);
  const [priceRange, setPriceRange] = useState([
    filters.precio_min || filterOptions?.precio_min || 0,
    filters.precio_max || filterOptions?.precio_max || 2000000
  ]);
  const priceRef = useRef(priceRange);
  const [openSections, setOpenSections] = useState({
    tipo: true,
    marca: true,
    precio: true,
    modelo: false,
    color: false
  });

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Debounced search preview count
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    
    if (!searchInput || searchInput === (filters.search || '')) {
      setSearchPreviewCount(null);
      return;
    }
    
    setSearchPreviewLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const params = { ...localFilters, search: searchInput, status: 'publicado' };
        delete params.page;
        const data = await api.getVehiclesCount(params);
        setSearchPreviewCount(data.count);
      } catch {
        setSearchPreviewCount(null);
      } finally {
        setSearchPreviewLoading(false);
      }
    }, 500);
    
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchInput]);

  const handleSectionToggle = (id, open) => {
    setOpenSections(prev => ({ ...prev, [id]: open }));
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handlePriceChange = (values) => {
    priceRef.current = values;
    setPriceRange(values);
  };

  const handlePriceCommit = (values) => {
    const v = values || priceRef.current;
    onFilterChange({
      ...localFilters,
      precio_min: v[0],
      precio_max: v[1]
    });
  };

  const handleClear = () => {
    setLocalFilters({});
    setSearchInput('');
    setSearchPreviewCount(null);
    setPriceRange([filterOptions?.precio_min || 0, filterOptions?.precio_max || 2000000]);
    onClearFilters();
  };

  const handleSearch = () => {
    const newFilters = { ...localFilters, search: searchInput || undefined };
    setLocalFilters(newFilters);
    setSearchPreviewCount(null);
    onFilterChange(newFilters);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const hasActiveFilters = Object.values(localFilters).some(v => v !== undefined && v !== '' && v !== null) || searchInput;

  // Inline JSX instead of a nested component to prevent focus loss
  const filterContent = (
    <div className="space-y-4">
      {/* Search */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar vehiculo..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyPress}
            className="pl-10 pr-20 bg-input border-transparent focus:border-primary"
            data-testid="filter-search"
          />
          <Button
            type="button"
            size="sm"
            onClick={handleSearch}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 px-3 bg-primary hover:bg-primary/90 text-white text-xs"
            data-testid="search-btn"
          >
            Buscar
          </Button>
        </div>
        {searchInput && searchInput !== (filters.search || '') && (
          <div className="flex items-center gap-1.5 text-xs px-1" data-testid="search-preview-count">
            {searchPreviewLoading ? (
              <><Loader2 className="w-3 h-3 animate-spin text-muted-foreground" /><span className="text-muted-foreground">Buscando...</span></>
            ) : searchPreviewCount !== null ? (
              <span className="text-primary font-medium">~{searchPreviewCount} resultados</span>
            ) : null}
          </div>
        )}
      </div>

      {/* Vehicle Count */}
      <div className="flex items-center justify-between py-2 border-b border-border">
        <span className="text-sm text-muted-foreground">Resultados</span>
        <span className="text-sm font-bold text-primary">{vehicleCount} vehiculos</span>
      </div>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleClear}
          className="w-full bg-destructive/10 border-destructive text-white hover:bg-destructive"
          data-testid="clear-filters"
        >
          <X className="w-4 h-4 mr-2" />
          Limpiar Filtros
        </Button>
      )}

      {/* Type Filter */}
      <FilterSection id="tipo" title="Tipo" isOpen={openSections.tipo} onToggle={handleSectionToggle}>
        <Select 
          value={localFilters.tipo || ''} 
          onValueChange={(value) => handleFilterChange('tipo', value === 'all' ? undefined : value)}
        >
          <SelectTrigger className="bg-input border-transparent" data-testid="filter-tipo">
            <SelectValue placeholder="Todos los tipos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            {filterOptions?.tipos?.map(tipo => (
              <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterSection>

      {/* Brand Filter */}
      <FilterSection id="marca" title="Marca" isOpen={openSections.marca} onToggle={handleSectionToggle}>
        <Select 
          value={localFilters.marca || ''} 
          onValueChange={(value) => handleFilterChange('marca', value === 'all' ? undefined : value)}
        >
          <SelectTrigger className="bg-input border-transparent" data-testid="filter-marca">
            <SelectValue placeholder="Todas las marcas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las marcas</SelectItem>
            {filterOptions?.marcas?.map(marca => (
              <SelectItem key={marca} value={marca}>{marca}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterSection>

      {/* Price Filter */}
      <FilterSection id="precio" title="Precio" isOpen={openSections.precio} onToggle={handleSectionToggle}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Minimo</Label>
              <Input
                type="number"
                value={priceRange[0]}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 0;
                  setPriceRange([val, priceRange[1]]);
                }}
                onBlur={handlePriceCommit}
                placeholder="$ Min"
                className="bg-input border-transparent focus:border-primary h-10 text-sm"
                data-testid="filter-precio-min"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Maximo</Label>
              <Input
                type="number"
                value={priceRange[1]}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 0;
                  setPriceRange([priceRange[0], val]);
                }}
                onBlur={handlePriceCommit}
                placeholder="$ Max"
                className="bg-input border-transparent focus:border-primary h-10 text-sm"
                data-testid="filter-precio-max"
              />
            </div>
          </div>
          
          <Slider
            value={priceRange}
            onValueChange={handlePriceChange}
            onValueCommit={handlePriceCommit}
            min={filterOptions?.precio_min || 0}
            max={filterOptions?.precio_max || 2000000}
            step={1000}
            data-testid="filter-precio-slider"
          />
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>{formatPrice(priceRange[0])}</span>
            <span>{formatPrice(priceRange[1])}</span>
          </div>
        </div>
      </FilterSection>

      {/* Year Filter */}
      <FilterSection id="modelo" title="Ano/Modelo" isOpen={openSections.modelo} onToggle={handleSectionToggle}>
        <div className="grid grid-cols-2 gap-2">
          <Select 
            value={localFilters.modelo_min?.toString() || ''} 
            onValueChange={(value) => handleFilterChange('modelo_min', value === 'all' ? undefined : parseInt(value))}
          >
            <SelectTrigger className="bg-input border-transparent" data-testid="filter-modelo-min">
              <SelectValue placeholder="Desde" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Desde</SelectItem>
              {filterOptions?.modelos?.slice().reverse().map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select 
            value={localFilters.modelo_max?.toString() || ''} 
            onValueChange={(value) => handleFilterChange('modelo_max', value === 'all' ? undefined : parseInt(value))}
          >
            <SelectTrigger className="bg-input border-transparent" data-testid="filter-modelo-max">
              <SelectValue placeholder="Hasta" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Hasta</SelectItem>
              {filterOptions?.modelos?.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </FilterSection>

      {/* Color Filter */}
      <FilterSection id="color" title="Color" isOpen={openSections.color} onToggle={handleSectionToggle}>
        <Select 
          value={localFilters.color || ''} 
          onValueChange={(value) => handleFilterChange('color', value === 'all' ? undefined : value)}
        >
          <SelectTrigger className="bg-input border-transparent" data-testid="filter-color">
            <SelectValue placeholder="Todos los colores" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los colores</SelectItem>
            {filterOptions?.colores?.map(color => (
              <SelectItem key={color} value={color}>{color}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterSection>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-72 flex-shrink-0">
        <div className="sticky top-24 bg-card border border-border rounded-sm p-6 space-y-4">
          <div className="flex items-center gap-2 pb-4 border-b border-border">
            <Filter className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold uppercase tracking-wider">Filtros</h2>
          </div>
          {filterContent}
        </div>
      </aside>

      {/* Mobile Filter Button & Sheet */}
      <div className="lg:hidden fixed bottom-6 right-6 z-40">
        <Sheet>
          <SheetTrigger asChild>
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-white rounded-full shadow-lg h-14 w-14"
              data-testid="mobile-filter-toggle"
            >
              <Filter className="w-6 h-6" />
              {hasActiveFilters && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-secondary rounded-full text-xs flex items-center justify-center">
                  !
                </span>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[320px] bg-card border-border overflow-y-auto">
            <SheetHeader className="pb-4 border-b border-border">
              <SheetTitle className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-primary" />
                <span className="text-lg font-bold uppercase tracking-wider">Filtros</span>
              </SheetTitle>
            </SheetHeader>
            <div className="py-6">
              {filterContent}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
};
